/**
 * Attachment Strategy Pattern for Import Operations
 *
 * This module defines the strategy interface for handling attachments during import
 * operations. Different backends (CloudKit vs Supabase) require different approaches
 * for attachment handling, which is abstracted through this strategy pattern.
 */

import type { UseMutationResult } from '@tanstack/react-query'
import type { Note, Change } from '../note'
import { SourceDatabase, NoteType } from '../note'
import type { ImportNote, ResolvedLocation } from './types'
import type { CreateOptions } from '../../hooks/useCreateNote'

import { supabaseClient } from '../../lib/SupabaseClient'
import { resolveFilenameConflicts } from './attachmentMerge'
import { MAX_ATTACHMENT_SIZE_BYTES } from './config'


/**
 * Strategy interface for handling attachments during import operations
 *
 * Different backends require different approaches:
 * - CloudKit: Atomic creation with attachments
 * - Supabase: Two-phase (create note, then upload attachments)
 */
export type AttachmentStrategy = {
  /**
   * Create a new note with initial content
   * For CloudKit: Can include attachments atomically
   * For Supabase: Creates note without attachments initially
   *
   * @param note - The note to create
   * @param location - Optional resolved location (filename and parent) for the note
   */
  create: (note: ImportNote, location?: ResolvedLocation) => Promise<Note>

  /**
   * Save/update note content with optional attachments
   * For CloudKit: Updates note with new attachments if provided
   * For Supabase: Updates note with attachment URLs after upload
   */
  save: (
    note: Note,
    content: string,
    attachments?: string[]
  ) => Promise<Note>

  /**
   * Optional first phase for backends that need to upload attachments after note creation
   * For CloudKit: No-op (attachments handled in create/save)
   * For Supabase: Uploads files to storage and generates signed URLs
   */
  uploadAttachmentsFirstPhase?: (note: Note, files: File[]) => Promise<AttachmentUploadResult>
}

/**
 * Result of attachment upload operations
 * Provides structured information about upload success/failure
 */
export type AttachmentUploadResult = {
  /** Number of attachments successfully uploaded */
  uploaded: number
  /** Number of attachments that failed to upload */
  failed: number
  /** Array of specific upload failures with file names and error messages */
  failures: AttachmentUploadFailure[]
}

/**
 * Details of individual attachment upload failures
 */
export type AttachmentUploadFailure = {
  /** Name of the file that failed to upload */
  filename: string
  /** Error message describing the failure */
  error: string
}

/**
 * Hook dependencies required by attachment strategies
 * These are the mutation hooks that strategies will use internally
 */
export type StrategyHooks = {
  useCreateNote: () => UseMutationResult<Note, Error, CreateOptions>
  useSaveNote: () => UseMutationResult<Note, Error, Change>
}



/**
 * Validate attachment files against size limits
 * Filters out oversized files and returns validation results
 *
 * @param files - Array of files to validate
 * @param maxSizeBytes - Maximum file size in bytes (default: 50MB)
 * @returns Validation result with valid files and oversized file warnings
 */
export function validateAttachments(
  files: File[],
  maxSizeBytes: number = MAX_ATTACHMENT_SIZE_BYTES
): { validFiles: File[]; oversizedFiles: AttachmentUploadFailure[] } {
  const validFiles: File[] = []
  const oversizedFiles: AttachmentUploadFailure[] = []

  for (const file of files) {
    if (file.size > maxSizeBytes) {
      const fileSizeMB = Math.round(file.size / 1024 / 1024)
      const maxSizeMB = Math.round(maxSizeBytes / 1024 / 1024)
      oversizedFiles.push({
        filename: file.name,
        error: `File exceeds ${String(maxSizeMB)}MB limit (${String(fileSizeMB)}MB)`
      })
    } else {
      validFiles.push(file)
    }
  }

  return { validFiles, oversizedFiles }
}

/**
 * Validate attachment files against the standard 50MB size limit
 * Convenience wrapper around validateAttachments with pre-configured limit
 *
 * @param files - Array of files to validate
 * @returns Validation result with valid files and oversized file warnings
 */
export const validateFileSizes = (files: File[]) => validateAttachments(files, MAX_ATTACHMENT_SIZE_BYTES)





/**
 * Factory function for creating CloudKit attachment strategy
 *
 * CloudKit supports atomic note creation with attachments, so the strategy
 * will handle attachments directly in the create() and save() methods.
 * The uploadAttachmentsFirstPhase() method validates file sizes for CloudKit.
 */
export const createCloudKitStrategy = (hooks: StrategyHooks): AttachmentStrategy => ({
  create: async (note: ImportNote, location?: ResolvedLocation): Promise<Note> => {
    // Validate attachment sizes (CloudKit 50MB limit)
    const { validFiles } = validateFileSizes(note.attachments)

    // Resolve filename conflicts with UUID prefixing
    const resolvedAttachments = resolveFilenameConflicts(validFiles)

    // Use the createNote hook with attachment support
    const createNoteMutation = hooks.useCreateNote()

    // Use provided location if available, otherwise fall back to extracting from path
    const filename = location?.filename ?? note.path.split('/').pop()?.replace(/\.[^./]+$/, '') ?? 'Untitled'
    // eslint-disable-next-line unicorn/no-null -- Required by CreateOptions type
    const parent = location?.parent ?? null

    return await createNoteMutation.mutateAsync({
      recordName: crypto.randomUUID(), // Generate unique record name
      noteType: note.isCalendar ? NoteType.CALENDAR_NOTE : NoteType.PROJECT_NOTE,
      parent,
      filename,
      content: await note.file.text(),
      attachments: resolvedAttachments,
    })
  },

  save: async (
    note: Note,
    content: string,
    attachments?: string[]
  ): Promise<Note> => {
    // Use the saveNote hook with attachment support
    const saveNoteMutation = hooks.useSaveNote()

    return await saveNoteMutation.mutateAsync({
      content,
      attachments,
      recordName: note.recordName,
      filename: note.filename,
      parent: note.parent,
      noteType: note.noteType,
      modificationDate: new Date(),
    })
  },

  uploadAttachmentsFirstPhase: (_note: Note, files: File[]): Promise<AttachmentUploadResult> => {
    // CloudKit handles attachments atomically, but we still validate file sizes
    const { validFiles, oversizedFiles } = validateFileSizes(files)

    return Promise.resolve({
      uploaded: validFiles.length,
      failed: oversizedFiles.length,
      failures: oversizedFiles
    })
  },
})

/**
 * Factory function for creating Supabase attachment strategy
 *
 * Supabase requires a two-phase approach:
 * 1. Create note without attachments
 * 2. Upload attachments to storage, generate signed URLs, update note content
 *
 * This strategy implements the two-phase pattern with proper error handling.
 */
export const createSupabaseStrategy = (hooks: StrategyHooks): AttachmentStrategy => ({
  create: async (note: ImportNote, location?: ResolvedLocation): Promise<Note> => {
    // Create note without attachments first (Supabase two-phase approach)
    const createNoteMutation = hooks.useCreateNote()

    // Use provided location if available, otherwise fall back to extracting from path
    const filename = location?.filename ?? note.path.split('/').pop()?.replace(/\.[^./]+$/, '') ?? 'Untitled'
    // eslint-disable-next-line unicorn/no-null -- Required by CreateOptions type
    const parent = location?.parent ?? null

    return await createNoteMutation.mutateAsync({
      recordName: crypto.randomUUID(), // Generate unique record name
      noteType: note.isCalendar ? NoteType.CALENDAR_NOTE : NoteType.PROJECT_NOTE,
      parent,
      filename,
      content: await note.file.text(),
      // No attachments in create phase for Supabase
    })
  },

  save: async (
    note: Note,
    content: string,
    attachments?: string[]
  ): Promise<Note> => {
    // Save note with attachment URLs after upload phase
    const saveNoteMutation = hooks.useSaveNote()

    return await saveNoteMutation.mutateAsync({
      content,
      attachments,
      recordName: note.recordName,
      filename: note.filename,
      parent: note.parent,
      noteType: note.noteType,
      modificationDate: new Date(),
    })
  },

  uploadAttachmentsFirstPhase: async (note: Note, files: File[]): Promise<AttachmentUploadResult> => {
    if (files.length === 0) {
      return { uploaded: 0, failed: 0, failures: [] }
    }

    // Validate file sizes (50MB limit for Supabase)
    const { validFiles, oversizedFiles } = validateFileSizes(files)

    // Resolve filename conflicts with UUID prefixing for valid files only
    const resolvedFiles = resolveFilenameConflicts(validFiles)
    const uploadedAttachments: { originalPath: string; filename: string }[] = []
    const uploadFailures: AttachmentUploadFailure[] = [...oversizedFiles]

    // Upload each file to Supabase storage
    for (const file of resolvedFiles) {
      try {
        // Upload the file to Supabase storage
        // Use upsert: true to handle duplicate attachments gracefully during import - 
        // overwrites existing files with same path instead of throwing 400/409 errors
        const { error: uploadError } = await supabaseClient.storage
          .from('note_attachments')
          .upload(`${note.recordName ?? 'unknown'}/${file.name}`, file, { upsert: true })

        if (uploadError && uploadError.message !== 'The resource already exists') {
          // File already exists is okay, other errors should be collected as failures
          uploadFailures.push({
            filename: file.name,
            error: `Upload failed: ${uploadError.message}`
          })
          continue // Continue with other files
        }

        uploadedAttachments.push({
          originalPath: file.name, // Use filename as original path for import context
          filename: file.name
        })

        // Attachment uploaded successfully - no logging needed to avoid console warnings
      } catch (error) {
        // Collect upload failure instead of throwing
        uploadFailures.push({
          filename: file.name,
          error: error instanceof Error ? error.message : 'Unknown upload error'
        })

        // Attachment upload failed - error already collected in uploadFailures array
      }
    }

    // If we have uploaded attachments, we need to update the note content with the new URLs
    if (uploadedAttachments.length > 0) {
      // Read the current note content
      const currentContent = note.content ?? ''

      // Keep original filenames in markdown instead of rewriting to signed URLs
      // This allows the runtime attachment system to resolve fresh URLs on each load
      const rewrittenContent = currentContent

      // Create attachment metadata for the note with blob URLs
      // This matches the original pre-consolidation behavior and prevents updateAttachments() from clearing the array
      const attachmentMetadata = uploadedAttachments.map(att => {
        const fileObject = files.find(f => f.name === att.filename)
        if (!fileObject) {
          throw new Error(`File not found for attachment: ${att.filename}`)
        }
        return JSON.stringify({
          title: att.filename,
          filename: att.filename,
          url: URL.createObjectURL(fileObject) // Temporary blob URL allows updateAttachments to work correctly
        })
      })

      // Update the note with rewritten content and attachment metadata
      const saveNoteMutation = hooks.useSaveNote()
      await saveNoteMutation.mutateAsync({
        content: rewrittenContent,
        attachments: attachmentMetadata,
        recordName: note.recordName,
        filename: note.filename,
        parent: note.parent,
        noteType: note.noteType,
        modificationDate: new Date(),
      })

      // Note updated with attachments successfully
    }

    // Return structured upload result
    return {
      uploaded: uploadedAttachments.length,
      failed: uploadFailures.length,
      failures: uploadFailures
    }
  },
})

/**
 * Factory function to create the appropriate strategy based on source database
 * 
 * @param backend - The source database type (CloudKit or Supabase)
 * @param hooks - The required mutation hooks
 * @returns The appropriate attachment strategy implementation
 */
export const createAttachmentStrategy = (
  backend: SourceDatabase,
  hooks: StrategyHooks
): AttachmentStrategy => {
  switch (backend) {
    case SourceDatabase.CLOUDKIT: {
      return createCloudKitStrategy(hooks)
    }
    case SourceDatabase.SUPABASE: {
      return createSupabaseStrategy(hooks)
    }
    default: {
      throw new Error(`Unsupported backend: ${String(backend)}`)
    }
  }
}
